import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSystemStats } from '../hooks/useSystemStats';
import { adminOffers as initialAdminOffers } from '../data/mockData';
import './Homepage.css';

// Extracted static data and functions
const LOGO_DATA = [
  { src: '/Bisu-logo.png', alt: 'BISU Logo' },
  { src: '/bohol-cacao-logo.png', alt: 'Bohol Cacao Logo' },
  { src: '/DOST-logo.png', alt: 'DOST Logo' },
];

const formatNumberFn = (num) => {
  if (num === 0 || num === null || num === undefined) return '0';
  return num.toLocaleString();
};

const SERVICES_STORAGE_KEY = 'bciis_admin_offers_v1';

// ServicesOfferedGrid as separate component for better memoization
const ServicesOfferedGrid = React.memo(({ offers }) => {
  if (!offers || offers.length === 0) {
    return <div className="text-center text-gray-500">No services available at the moment.</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {offers.map((o) => (
        <div key={o.id} className="bg-amber-50 border border-amber-100 rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold text-amber-900">{o.title}</h3>
          <p className="text-sm text-gray-700 mt-2">{o.description}</p>
          <p className="text-xs text-gray-500 mt-3">Contact: {o.contact || '—'}</p>
        </div>
      ))}
    </div>
  );
});

export function Homepage() {
  const navigate = useNavigate();
  const { farmerCount, processorCount, municipalityCount, totalHectares, loading: statsLoading, error: statsError } = useSystemStats();

  // Memoized navigation callbacks
  const handleViewFarmers = useCallback(() => navigate('/farmers'), [navigate]);
  const handleViewProcessors = useCallback(() => navigate('/processors'), [navigate]);
  const handleViewMap = useCallback(() => navigate('/map'), [navigate]);
  const handleViewAnalytics = useCallback(() => navigate('/analytics'), [navigate]);

  // Memoized stats display calculation
  const statsDisplay = useMemo(() => [
    { 
      label: 'Registered Farmers', 
      value: statsLoading ? '...' : formatNumberFn(farmerCount),
      loading: statsLoading
    },
    { 
      label: 'Active Processors', 
      value: statsLoading ? '...' : formatNumberFn(processorCount),
      loading: statsLoading
    },
    { 
      label: 'Municipalities Covered', 
      value: statsLoading ? '...' : formatNumberFn(municipalityCount),
      loading: statsLoading
    },
    { 
      label: 'Total Hectares', 
      value: statsLoading ? '...' : formatNumberFn(totalHectares),
      loading: statsLoading
    },
  ], [statsLoading, farmerCount, processorCount, municipalityCount, totalHectares]);

  // Loaded services state with optimization
  const [offers, setOffers] = React.useState([]);

  React.useEffect(() => {
    const stored = localStorage.getItem(SERVICES_STORAGE_KEY);
    if (stored) {
      try {
        setOffers(JSON.parse(stored));
        return;
      } catch (e) {
        // fallback
      }
    }
    setOffers(initialAdminOffers || []);
  }, []);

  return (
    <div className="min-h-screen">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-900 via-orange-800 to-green-800 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left" style={{ opacity: 0, animationFillMode: 'forwards' }}>
              {/* Static Logos */}
              <div className="flex items-center gap-3 mb-4">
                {LOGO_DATA.map((logo) => (
                  <img
                    key={logo.alt}
                    src={logo.src}
                    alt={logo.alt}
                    className="rounded-full border-2 border-amber-300 object-cover"
                    style={{ width: 48, height: 48 }}
                    loading="eager"
                    decoding="sync"
                  />
                ))}
              </div>
              <h1 className="mb-6 text-white text-5xl font-bold leading-tight">
                Welcome to{" "}
                <span className="gradient-text-animated">
                  Bohol Cacao Industry Information System
                </span>
              </h1>
              <p className="mb-8 text-amber-100 text-lg leading-relaxed animate-fade-in-up" style={{ opacity: 0, animationDelay: '0.2s', animationFillMode: 'forwards' }}>
                Manage and analyze cacao farmers and processors data across Bohol with data-driven insights.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-3 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  onClick={handleViewFarmers}
                >
                  Explore Farmers
                </Button>
                <Button 
                  className="bg-black text-white hover:bg-gray-800 rounded-full px-8 py-3 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  onClick={handleViewProcessors}
                >
                  View Processors
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-3 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  onClick={handleViewMap}
                >
                  Interactive Map
                </Button>
              </div>
            </div>
            <div className="relative h-80 md:h-96 shadow-2xl overflow-hidden rounded-2xl border-4 border-amber-300 animate-slide-in-right">
              <img
                src="/cacao-images/boholcacao2.jpg"
                alt="Bohol Cacao"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="mb-4 text-amber-900">Platform Overview</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real-time insights from Bohol's cacao industry
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsError ? (
              <div className="col-span-full text-center py-6">
                <p className="text-amber-700 font-medium">{statsError}</p>
              </div>
            ) : (
              statsDisplay.map((stat, index) => (
              <Card key={index} className="text-center border-amber-200 hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className={`text-2xl text-amber-900 ${stat.loading ? 'animate-pulse' : ''}`}>
                    {stat.value}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{stat.label}</p>
                </CardContent>
              </Card>
            ))
            )}
          </div>
        </div>
      </section>

      {/* Features Section with Bisubilar Background */}
      <section className="py-16 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: 'url(/background/bisubilar.jpg)',
            backgroundPosition: 'center top',
            filter: 'brightness(0.7)'
          }}
        ></div>
        
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/25"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="mb-4 text-white drop-shadow-lg text-5xl font-extrabold">Key Features</h2>
            <p className="text-white/90 max-w-2xl mx-auto drop-shadow-md text-lg">
              Tools for managing and analyzing Bohol's cacao industry
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Glassmorphism Card 1 */}
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Interactive Map</h3>
                <p className="text-white/80 mb-4">
                  View geographical distribution of farmers and processors across Bohol municipalities.
                </p>
                <Button 
                  className="mt-4 bg-green-600/80 hover:bg-green-700 text-white w-full backdrop-blur-sm border border-white/30"
                  onClick={handleViewMap}
                >
                  View Map
                </Button>
              </div>
            </div>
            
            {/* Glassmorphism Card 2 */}
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Data Analytics</h3>
                <p className="text-white/80">
                  Charts and visualizations for demographics, production trends, and market insights.
                </p>
              </div>
            </div>
            
            {/* Glassmorphism Card 3 */}
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Advanced Search</h3>
                <p className="text-white/80">
                  Filter farmers by location, ownership, and capacity. Search processors by type and reach.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-8">
            {/* Glassmorphism Card 4 */}
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Profile Management</h3>
                <p className="text-white/80">
                  Detailed profiles with demographics, business status, and production data.
                </p>
              </div>
            </div>

            {/* Glassmorphism Card 5 */}
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg shadow-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Geographic Insights</h3>
                <p className="text-white/80">
                  Municipality-level statistics and regional patterns in cacao farming and processing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered (Admin-provided) */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-amber-900">Services Offered</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Services and programs provided by the platform and administrators.</p>
          </div>
          <ServicesOfferedGrid offers={offers} />
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-amber-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-white">Ready to Explore?</h2>
          <p className="mb-8 text-amber-100 max-w-2xl mx-auto">
            Discover Bohol's network of cacao farmers and processors.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white border-2 border-black"
              onClick={handleViewFarmers}
            >
              Browse Farmer Profiles
            </Button>
            <Button 
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={handleViewMap}
            >
              Explore Interactive Map
            </Button>
            <Button 
              className="bg-black text-white hover:bg-gray-800"
              onClick={handleViewAnalytics}
            >
              View Analytics Dashboard
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default React.memo(Homepage);
